/*!
 * RBF Cosmetics — Core Entry Point
 * ------------------------------------------------------------------
 * Loaded as a single <script type="module" defer> from
 * snippets/rbf-scripts.liquid. This file does almost no work itself —
 * its only job is FEATURE DETECTION + LAZY LOADING: it checks which
 * data-rbf-* hooks are actually present on the current page, and
 * only then dynamically imports the corresponding feature module.
 *
 * This is the mechanism behind "everything should be lazy-loaded
 * where appropriate": a page with no product gallery never downloads
 * rbf-gallery.js; a page with no search UI never downloads
 * rbf-search.js. Native dynamic import() is used rather than a
 * bundler-based code-splitting setup, since this theme has no build
 * step — every rbf-*.js file IS the shippable asset.
 *
 * Modules that are needed on virtually every page (navigation,
 * accessibility) are imported eagerly but are themselves tiny; heavy,
 * page-specific interaction logic (gallery, search, forms) is always
 * behind a feature check below.
 * ------------------------------------------------------------------
 */
import { ready, qs } from './rbf-utils.js';

ready(function init() {
  // Navigation is present on every page (header/mega menu/mobile menu)
  // so it is imported unconditionally, but it is still a separate,
  // small module rather than being inlined here.
  import('./rbf-nav.js').then(function (mod) { mod.init(); });

  // Cart drawer markup is rendered globally by the header/layout, so
  // its trigger hook is present sitewide — imported unconditionally.
  if (qs('[data-rbf-cart-drawer]')) {
    import('./rbf-cart.js').then(function (mod) { mod.init(); });
  }

  // Predictive search only loads its (heavier) module when a search
  // trigger actually exists on the rendered page.
  if (qs('[data-rbf-search-trigger]') || qs('[data-rbf-search-input]')) {
    import('./rbf-search.js').then(function (mod) { mod.init(); });
  }

  // Recent searches + matched-term highlighting layer on top of the
  // theme's native predictive search wherever a search form renders.
  if (qs('.gsp-search-form')) {
    import('./rbf-search-enhance.js').then(function (mod) { mod.init(); });
  }

  // Product gallery only loads on PDP templates that render the hook.
  if (qs('[data-rbf-gallery]')) {
    import('./rbf-gallery.js').then(function (mod) { mod.init(); });
  }

  // Quick Add only loads where at least one product card renders the
  // control (collection/search/recommendation grids).
  if (qs('[data-rbf-quick-add]')) {
    import('./rbf-quick-add.js').then(function (mod) { mod.init(); });
  }

  // Article Table of Contents — only loads on article pages that
  // render the TOC placeholder (sections/rbf-article-meta.liquid).
  if (qs('[data-rbf-toc]')) {
    import('./rbf-article-toc.js').then(function (mod) { mod.init(); });
  }

  // Bundle Selector / Routine Builder "add all" — only loads on pages
  // that actually render a bundle add-to-cart control.
  if (qs('[data-rbf-bundle-add]')) {
    import('./rbf-bundle.js').then(function (mod) { mod.init(); });
  }

  // Forms module (newsletter / contact / stockist application) only
  // loads when a form carrying the hook is present.
  if (qs('[data-rbf-form]')) {
    import('./rbf-forms.js').then(function (mod) { mod.init(); });
  }

  // FAQ Hub search filter — only loads on pages with the FAQ search input.
  if (qs('[data-rbf-faq-search-input]')) {
    import('./rbf-faq-search.js').then(function (mod) { mod.init(); });
  }

  // Generic scroll-reveal + accordion wiring used by many static
  // sections; small enough to load unconditionally once other
  // heavier per-feature checks above have run.
  if (qs('[data-rbf-animate]') || qs('[data-rbf-accordion]')) {
    import('./rbf-reveal.js').then(function (mod) { mod.init(); });
  }
});
